import {
  Component, Input, OnDestroy,
  OnInit, SimpleChanges, ViewEncapsulation,
  ChangeDetectionStrategy
} from "@angular/core";
import { ParamMap } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import FileSaver from "file-saver";
import { NGXLogger } from "ngx-logger";
import { BehaviorSubject, Subscription } from "rxjs";
import { AcquisitionLogColumns } from "src/app/enums/acquisition-log/acquisition-log-columns";
import { ActionState } from "src/app/enums/action-state.enum";
import { AlarmsColumns } from "src/app/enums/alarms/alarms-columns";
import { TableAcquisitionLogListMapper } from "src/app/mapper/table/table-acquisition-log-list.mapper";
import { DisplayText } from "src/app/models/events/displayText";
import { EventsActionFilters } from "src/app/models/events/eventsActionFilters";
import { EventColumn } from "src/app/models/events/eventsColumn";
import { AcquisitionLogRepo } from "src/app/repositories/acquistion-log-repo";
import { AlarmRepo } from "src/app/repositories/alarm-repo";
import { AlertService } from "src/app/services";
import {
  AlertParameters,
  AlertType
} from "src/app/shared/models/models-index";
import { EventEmitterFilters } from "src/app/shared/models/table/event-emitter-filters";
import FiltersConfig from "src/app/shared/models/table/filters-config";
import { IconType } from "src/app/tinea-components/icon/enum/icon-type";
import { LinkClickSateType } from "src/app/tinea-components/paging/enum/link-click-state-type";
import { PagingModel } from "src/app/tinea-components/paging/models/input/paging-model";
import { TableModel } from "src/app/tinea-components/table/models/input/table-model";
import { UiIcon } from "src/app/tinea-components/table/models/input/ui-icon";
import { TableOutputEvent } from "src/app/tinea-components/table/models/table-output-event";
import { TableOutputRowEvent } from "src/app/tinea-components/table/models/table-output-row-event";
import { coreEnvironment } from "src/environments/core/core.environment";
import {
  AgentEventSortFieldName,
  AgentEventType, AgentsEventsPaginatedList, AgentsEventsSearch, AlarmSearchFilterParams, ApplicationFeature, MatchingType, TicketSearchFilter
} from 'src/swagger-gen/web-secured/models';
import { ConnectionType } from "../../models/conection-type";

@Component({
    selector: "app-acquisition-log-list",
    templateUrl: "./acquisition-log-list.component.html",
    styleUrls: ["./acquisition-log-list.component.scss"],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AcquisitionLogListComponent implements OnInit, OnDestroy {
  @Input() allowGlobalActions: boolean;
  @Input() filters: ParamMap;

  FEATURE = ApplicationFeature;

  table: TableModel;
  paging: PagingModel;

  actionOnAlarms: boolean = coreEnvironment.actionOnAlarms;
  connectionTypeList = Object.values(ConnectionType);
  connectionTypeSelected: any;

  subTypeList = Object.values(AgentEventType);
  subTypeSelected: any;

  acquisitionLogs: AgentsEventsPaginatedList;
  filtersConfig: FiltersConfig;
  fullColumnList: Array<EventColumn>;

  matchingType = MatchingType.EXACT_MATCH_IGNORE_CASE;
  newItemFilter: EventsActionFilters = new EventsActionFilters();
  dataForChildComponent: any = {};

  loadingState = new BehaviorSubject(ActionState.LOADING);
  actionState = ActionState;

  private subscriptions: Subscription[] = [];
  errorMessage: string;
  acknowledgingAll = false;

  offsetEvent = 0;
  limitEvent = 25;

  isoldTableView: boolean;

  constructor(
    private logger: NGXLogger,
    private alarmsService: AlarmRepo,
    private acquisitionLogRepo: AcquisitionLogRepo,
    private tableAcquisitionLogMapper: TableAcquisitionLogListMapper,
    private translate: TranslateService,
    private alertService: AlertService
  ) {
    this.dataForChildComponent.body = {
      filters: [],
    };
  }

  ngOnInit() {
    this.fullColumnList = [
      new EventColumn(
        true,
        new DisplayText(
          AcquisitionLogColumns.DATE,
          this.translate.instant("acquisition-log.table.date")
        )
      ),
      new EventColumn(
        true,
        new DisplayText(
          AcquisitionLogColumns.TYPE,
          this.translate.instant("acquisition-log.table.type")
        )
      ),
      new EventColumn(
        true,
        new DisplayText(
          AcquisitionLogColumns.SUBTYPE,
          this.translate.instant("acquisition-log.table.subType")
        )
      ),
      new EventColumn(
        true,
        new DisplayText(
          AcquisitionLogColumns.CONNECTION_TYPE,
          this.translate.instant("acquisition-log.table.connectionType")
        )
      ),
      new EventColumn(
        true,
        new DisplayText(
          AcquisitionLogColumns.CONNECTION_NAME,
          this.translate.instant("acquisition-log.table.connectionName")
        )
      ),
      new EventColumn(
        true,
        new DisplayText(
          AcquisitionLogColumns.CONNECTION_ID,
          this.translate.instant("acquisition-log.table.connectionId")
        )
      ),
      new EventColumn(
        true,
        new DisplayText(
          AcquisitionLogColumns.TAG_NAME,
          this.translate.instant("acquisition-log.table.tagName")
        )
      ),
      new EventColumn(
        true,
        new DisplayText(
          AcquisitionLogColumns.DATA,
          this.translate.instant("acquisition-log.table.data")
        )
      ),
      new EventColumn(
          true,
          new DisplayText(
              AcquisitionLogColumns.STATION,
              this.translate.instant("acquisition-log.station")
          )
      ),
    ];
    this.filtersConfig = {
      date: true,
      column: {
        fullColumnList: this.fullColumnList,
      },
      sort: {
        paramsToInclude: Object.values(AgentEventSortFieldName),
        defaultSort: AgentEventSortFieldName.DATE,
      },
      search: {
        paramsToInclude: [
          'ALL',
          'CONNECTION_NAME',
          'DATA',
          'STATION',
          'TAG_NAME',
        ],
      },
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filters && changes.filters.currentValue) {
      // TODO
      // this.dataForChildComponent.body.filters = this.getFiltersFromParams(this.filters);
    }
  }

  onChangeConnectionType(event: any) {
    this.connectionTypeSelected = event;
    this.getAcquisitionLog();
  }

  onChangeEventType(event: any) {
    this.subTypeSelected = event;
    this.getAcquisitionLog();
  }

  /**
   * Making a table filters list from parameters in url.
   * @param params params from url
   * @param type caller page type used for translations
   * @returns a list of filters, for now, only AlarmSearchFilterParams, but you can add any other
   */
  getFiltersFromParams(params: ParamMap): AlarmSearchFilterParams[] {
    const keys: string[] = params.keys;
    // this.loggerService.trace('keys', keys);
    const filters: AlarmSearchFilterParams[] = [];
    keys.forEach((key) => {
      if (key === "states") {
        const states: string[] = params.get(key).split(",");
        // this.loggerService.trace('keys', states);
        states.forEach((state) => {
          // this.loggerService.trace('state', state);
          const filter: any = {
            value: state,
            field: TicketSearchFilter.STATE,
            matchingType: MatchingType.EXACT_MATCH_IGNORE_CASE,
          };

          // this.loggerService.trace('state filter', filter);
          filters.push(filter);
        });
      }
    });

    return filters;
  }

  /***************************************  APPEL API  **********************************/
  getAcquisitionLog() {
    const params = this.dataForChildComponent.params || [];
    const filterBody = this.getBody();

    let search = '';
    const body: AgentsEventsSearch = {};

    this.loadingState.next(ActionState.LOADING);
    this.isoldTableView = false;

    const searchFilter = filterBody.filters.find(f => f.field === 'ALL')?.value;
    if (searchFilter) {
      search = searchFilter;
    }

    const connectionNameFilter = filterBody.filters.find(f => f.field === 'CONNECTION_NAME')?.value;
    if (connectionNameFilter) {
      body.connectionName = connectionNameFilter;
    }

    const dataFilter = filterBody.filters.find(f => f.field === 'DATA')?.value;
    if (dataFilter) {
      body.data = dataFilter;
    }

    const stationFilter = filterBody.filters.find(f => f.field === 'STATION')?.value;
    if (stationFilter) {
      body.stationName = stationFilter;
    }

    const tagNameFilter = filterBody.filters.find(f => f.field === 'TAG_NAME')?.value;
    if (tagNameFilter) {
      body.tagName = tagNameFilter;
    }

    const typeParam = params.find(p => p.key === 'eventType')?.value;
    if (typeParam) {
      body.type = [typeParam];
    }

    if (this.connectionTypeList.includes(this.connectionTypeSelected)) {
      body.connectionType = [this.connectionTypeSelected];
    }

    if (this.subTypeList.includes(this.subTypeSelected)) {
      body.subType = [this.subTypeSelected];
    }

    const startDateTime = params.find(p => p.key === 'startDateTime')?.value ?? '';
    const endtDateTime = params.find(p => p.key === 'endDateTime')?.value ?? '';
    const sortFieldName = params.find(p => p.key === 'sortFieldName')?.value ?? '';
    const sortDirection = params.find(p => p.key === 'sortDirection')?.value ?? '';

    this.acquisitionLogRepo
      .getAcquisitionLogs(body, this.limitEvent, this.offsetEvent, startDateTime, endtDateTime, sortFieldName, sortDirection, search)
      .toPromise()
      .then(
        (events: AgentsEventsPaginatedList) => {
          this.handleGetAcquisitionLogResponse(events);
        },
        (error) => {
          this.handleGetAcquisitionLogErrors(error);
        }
      );
  }

  getBody() {
    // TEMP waiting for better solution, see afterGetTickets()
    // if (this.stationName) {
    //   const filter: TicketSearchFilterParams = {
    //     value: this.currentStationName,
    //     field: 'STATION_NAME',
    //     matchingType: 'EXACT_MATCH'
    //   };
    //   this.dataForChildComponent.body.filters.push(filter);
    // }

    return this.dataForChildComponent.body;
  }

  handleGetAcquisitionLogResponse(acquisitionLogs: AgentsEventsPaginatedList) {
    this.acquisitionLogs = acquisitionLogs;
    this.loadingState.next(ActionState.READY);
    this.createTableMapping();
    this.createPagingMapping();
  }

  handleGetAcquisitionLogErrors(error) {
    this.loadingState.next(ActionState.READY);
    this.logger.log(error);
    const alertParams = new AlertParameters(
      false,
      false,
      true,
      false,
      false,
      false,
      true,
      this.translate.instant("error.title")
    );
    this.alertService.showAlert(error, AlertType.ERROR, alertParams);
  }

  onNewAlarmList() {
    this.createTableMapping();
  }

  /***************************************  PAGING MAPPING  **********************************/
  createPagingMapping() {
    this.paging = this.generatePaging();
  }

  generatePaging(): PagingModel {
    const paging = new PagingModel(
      this.acquisitionLogs.paging.offset,
      this.limitEvent,
      this.acquisitionLogs.paging.curItems,
      this.acquisitionLogs.paging.totalItems,
      this.acquisitionLogs.paging.nextItemsLink,
      this.acquisitionLogs.paging.previousItemsLink
    );

    // configuration
    paging.configuration.actionIfNoLink = "disabled";
    paging.configuration.pagingEnabled = false;
    paging.configuration.rangeEnabled = true;
    // prevLink
    const prevLinkElement = new UiIcon();
    prevLinkElement.type = IconType.FONTAWESOME;
    prevLinkElement.value = "icon-arrow-left-circle";
    paging.configuration.prevLinkElement = prevLinkElement;
    // nextLink
    const nextLinkElement = new UiIcon();
    nextLinkElement.type = IconType.FONTAWESOME;
    nextLinkElement.value = "icon-arrow-right-circle";
    paging.configuration.nextLinkElement = nextLinkElement;

    return paging;
  }

  eventOnClickPrevLink(event: LinkClickSateType): void {
    if (event === LinkClickSateType.ALLOWED) {
      this.onPagination("prev-event");
    }
  }

  eventOnClickNextLink(event: LinkClickSateType): void {
    if (event === LinkClickSateType.ALLOWED) {
      this.onPagination("next-event");
    }
  }

  // TODO: refacto to Paging Component
  onPagination(type: "next-event" | "prev-event") {
    if (type === "next-event") {
      this.onNextPaginationEvent();
    } else if (type === "prev-event") {
      this.onPrevPaginationEvent();
    }
  }

  onPrevPaginationEvent() {
    this.offsetEvent = this.offsetEvent - this.limitEvent;
    this.getAcquisitionLog();
  }

  onNextPaginationEvent() {
    this.offsetEvent = this.offsetEvent + this.limitEvent;
    this.getAcquisitionLog();
  }

  /***************************************  TABLE MAPPING  **********************************/
  createTableMapping() {
    const table: TableModel = this.tableAcquisitionLogMapper.init(this.acquisitionLogs);
    this.table = table;
  }

  onMouseenterRowEventOfTable(event: TableOutputRowEvent) {
    if (event.isDataRowTarget()) {
      // animation on column ACTION
      // const rowId = event.row.id;
      // const dataRow = this.table.findDataRowById(rowId);
      // const cell = dataRow.findCellByColumnId(
      //   this.tableAlarmListMapper.COLUMN_ID_ACTION
      // );
      // const valueCell = cell.findValueCellByDataId(this.tableAlarmListMapper.ID_EVENT_HISTORY);
      // valueCell.displayedValue = this.translate.instant('action.history');
      // const css = this.rightService.isDisabled(ApplicationFeature.SITE_OPERATION) ? ' d-none' : '';
      // valueCell.cssClass = css;
      // Désactivé par demande HAJA - boutons restore & acquit
      // const valueCell = cell.findValueCellByDataId(
      //   this.tableAlarmListMapper.ID_ACQUIT_ALARM
      // );
      // valueCell.displayedValue =
      //   dataRow.row.obj.state === AlarmState.RAISED
      //     ? this.translate.instant('action.acquit')
      //     : this.translate.instant('action.restore');
      // valueCell.cssClass =
      //   dataRow.row.obj.state === AlarmState.RAISED
      //     ? this.tableAlarmListMapper.CSS_CLASS_ACQUIT
      //     : this.tableAlarmListMapper.CSS_CLASS_RESTORE;
    }
  }

  onMouseleaveRowEventOfTable(event: TableOutputRowEvent) {
    if (event.isDataRowTarget()) {
      // animation on column ACTION
      // const rowId = event.row.id;
      // const dataRow = this.table.findDataRowById(rowId);
      // const cell = dataRow.findCellByColumnId(
      //   this.tableAlarmListMapper.COLUMN_ID_ACTION
      // );
      // const valueCell = cell.findValueCellByDataId(
      //   this.tableAlarmListMapper.ID_EVENT_HISTORY
      // );
      // valueCell.displayedValue = ' ';
      // valueCell.cssClass = '';
      // Désactivé par demande HAJA - boutons restore & acquit
      // const valueCell = cell.findValueCellByDataId(
      //   this.tableAlarmListMapper.ID_ACQUIT_ALARM
      // );
      // valueCell.displayedValue = ' ';
      // valueCell.cssClass =
      //   dataRow.row.obj.state === AlarmState.RAISED
      //     ? this.tableAlarmListMapper.CSS_STATE_RAISED
      //     : '';
    }
  }

  onEventOnClick(event: TableOutputEvent) {}

  /***************************************  FILTERS EVENT  **********************************/
  eventReceiver(event: any) {
    const typeEvent = event.type;
    const dataEvent = event.data;
    switch (typeEvent) {
      case EventEmitterFilters.PARAMS:
        this.getParamsForGetEvents(dataEvent);
        break;
      case EventEmitterFilters.BODY:
        this.getBodyForGetEvents(dataEvent);
        break;
      case EventEmitterFilters.SHOW_HIDE_COLUMN:
        this.addRemoveColumnItem(dataEvent);
        break;
      case EventEmitterFilters.EXPORT:
        this.exportData();
        break;
      default:
        return;
    }
  }

  exportData() {
    this.filtersConfig.export.isLoading = true;
    const params = this.dataForChildComponent.params || [];

    this.alarmsService
      .exportAlarms(params)
      .toPromise()
      .then((response) => {
        // this.loggerService.trace ('in StationTicketsTableComponent getTickets(): got tickets');
        const filename = response.headers.get("content-disposition").split("=");
        const blob = new Blob([response.body], {
          type: response.headers.get("content-type"),
        });
        FileSaver.saveAs(blob, filename[1].slice(1, -1));
        this.filtersConfig.export.isLoading = false;
      })
      .catch((error) => {
        this.filtersConfig.export.isLoading = false;
        const title = this.translate.instant("error.title");
        const alertParams = new AlertParameters(
          false,
          false,
          true,
          false,
          false,
          false,
          true,
          title
        );
        this.alertService.showAlert(error, AlertType.ERROR, alertParams);
      });
  }

  getParamsForGetEvents(event: any): void {
    // this.loggerService.log('in getParamsForGetEvents');
    this.dataForChildComponent.params = event;
    this.getAcquisitionLog();
  }

  getBodyForGetEvents(event: any): void {
    // this.loggerService.log('in getBodyForGetEvents');
    this.dataForChildComponent.body = event;
    this.getAcquisitionLog();
  }

  addRemoveColumnItem(event: any): void {
    const columnKey = event.columnKey;
    const show = event.show;
    this.table.dataRow.forEach((r) => {
      r.cells.forEach((c) => {
        this.showOrHideCell(c, columnKey, show);
      });
    });
    this.addRemoveColumnToTableHeader(columnKey, show);
  }

  addRemoveColumnToTableHeader(columnKey: string, show: boolean) {
    this.table.header.cells.forEach((c) => {
      this.showOrHideCell(c, columnKey, show);
    });
  }

  showOrHideCell(c: any, columnKey: string, show: boolean) {
    const types = AcquisitionLogColumns.values();
    types.forEach((type) => {
      if (columnKey === type && c.columnId === type) {
        c.show = show;
      }
    });
  }

  ngOnDestroy() {
    this.acquisitionLogs = null;
    if (this.subscriptions !== undefined) {
      this.subscriptions.forEach((s) => s.unsubscribe());
    }
  }
}
